import { BillGroupInDunningResponse, IDunnings } from '@monetize/types/app';
import {
  ApiListResponse,
  DEFAULT_PAGER,
  GetListApiFilter,
  TDataTablePager,
} from '@monetize/types/common';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, handleApiErrorToast } from '../../../../api/axios';
import {
  useDeleteEntity,
  useGetListData,
  usePerformEntityAction,
} from '../../../../api/queryUtils';
import {
  MButton,
  MFlex,
  MHStack,
  MPageContainer,
  MPageSearchInput,
  MSettingsPageHeader,
} from '../../../../components/Monetize';
import { DataTableActions } from '../../../../components/Monetize/DataTable/MDataTableActions';
import { useTableColumns } from '../../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../../components/Monetize/DataTableNew/DataTable';
import { DataTableRef } from '../../../../components/Monetize/DataTableNew/DataTableTypes';
import { ACTION_COLUMN_KEY } from '../../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { ROUTES } from '../../../../constants';
import { useCloneDunning } from '../../../../hooks/useCloneDunning';
import { useConfirmModal } from '../../../../services/confirmModal';
import { useDocumentHead } from '../../../../services/documentHead';
import { useToast } from '../../../../services/toast';
import {
  buildFilterParamsRequestObject,
  getIsTrulyEmptyList,
} from '../../../../utils';
import { navigateOrOpenNewWindow } from '../../../../utils/misc';
import {
  DUNNING_LIST_CATALOG,
  DunningListColumnContext,
} from './dunningList.columns';

const defaultPager: TDataTablePager = {
  ...DEFAULT_PAGER,
  sortField: 'name',
  sortOrder: 1,
};

export const DunningPage = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { setDocTitle } = useDocumentHead();
  const { showConfirmModal, setModalLoading, closeModal } = useConfirmModal();
  const [searchKey] = useState<string>('name');
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const [filterParams, setFilterParams] = useState<GetListApiFilter>(() =>
    buildFilterParamsRequestObject([], globalFilter, searchKey),
  );
  const [pager, setPager] = useState<TDataTablePager>(defaultPager);
  const [billGroupsCounts, setBillGroupsCounts] = useState<
    Record<string, number>
  >({});

  const tableRef = useRef<DataTableRef>(null);

  const { data: dunningProcesses, isLoading: loading } =
    useGetListData<IDunnings>('dunnings', {
      config: pager,
      filters: filterParams,
    });

  const { mutate: deleteDunningProcess } = useDeleteEntity<Response>(
    'dunnings',
    { meta: { showErrorToast: true } },
  );
  const { mutate: updateDefaultDunningProcess } =
    usePerformEntityAction<IDunnings>('dunnings', {
      meta: { showErrorToast: true },
    });

  const { mutate: cloneDunning, isPending: isCloningDunning } = useCloneDunning(
    {
      onSuccess: (clonedDunning) => {
        if (clonedDunning.id) {
          navigate(ROUTES.getDunningEditRoute(clonedDunning.id));
        }
      },
    },
  );

  const handleCloneDunning = (dunning: IDunnings) => {
    cloneDunning(dunning.id);
  };

  // Fetch bill groups count for each dunning process
  useEffect(() => {
    if (dunningProcesses?.content?.length) {
      const fetchBillGroupsCounts = async () => {
        try {
          const newCounts = await Promise.all(
            dunningProcesses.content.map((dunning) =>
              apiGet<ApiListResponse<BillGroupInDunningResponse>>(
                `/api/dunning/${dunning.id}/billGroups`,
              ).then((response) => [dunning.id, response.data.totalElements]),
            ),
          ).then((responses) => {
            return responses.reduce(
              (acc: Record<string, number>, [dunningId, totalElements]) => {
                acc[dunningId] = Number(totalElements) || 0;
                return acc;
              },
              {},
            );
          });

          setBillGroupsCounts(newCounts);
        } catch (error) {
          handleApiErrorToast(error);
        }
      };

      fetchBillGroupsCounts();
    }
  }, [dunningProcesses?.content]);

  useEffect(() => {
    setFilterParams(
      buildFilterParamsRequestObject([], globalFilter, searchKey),
    );
  }, [pager, globalFilter, searchKey]);

  useEffect(() => {
    setDocTitle('Settings', 'Dunning');
  }, [setDocTitle]);

  const redirectToCreate = () => navigate(ROUTES.SETTINGS_DUNNING_NEW);

  const handleDeleteDunning = useCallback(
    (payload: IDunnings) => {
      const onYes = () => {
        setModalLoading(true);
        deleteDunningProcess(
          { id: payload.id },
          {
            onSuccess: (data) => {
              if (data.status === 200) {
                addToast({
                  summary: 'Dunning Process Deleted',
                  detail: `${payload.name} has successfully been deleted.`,
                  severity: 'success',
                });
                closeModal();
                setModalLoading(false);
              }
            },
          },
        );
      };

      showConfirmModal({
        title: `Are you sure you want to delete this dunning process?`,
        description:
          'Once deleted, this dunning process will be removed from any Bill Groups it is currently assigned.',
        onYes,
        yesBtnProps: {
          variant: 'delete',
        },
        noBtnProps: {
          variant: 'cancel',
        },
      });
    },
    [
      addToast,
      closeModal,
      deleteDunningProcess,
      setModalLoading,
      showConfirmModal,
    ],
  );

  const handleUpdateDefaultDunningProcess = useCallback(
    (payload: IDunnings) => {
      updateDefaultDunningProcess({ id: payload.id, action: 'default' });
    },
    [updateDefaultDunningProcess],
  );

  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading,
    totalElements: dunningProcesses?.totalElements || 0,
    filters: [],
    searchTerm: globalFilter,
    page: pager.page,
  });

  const actionsTemplate = useCallback(
    (dunning: IDunnings) => {
      const actions = [
        ...(dunning.defaultProcess
          ? []
          : [
              {
                title: 'Set as Default',
                enabled: true,
                action: () => handleUpdateDefaultDunningProcess(dunning),
              },
            ]),
        {
          title: 'View Assigned Bill Groups',
          enabled: true,
          action: () =>
            navigate(
              `${ROUTES.BILLING_DUNNING_MANAGEMENT}?dunningProcessId=${dunning.id}`,
            ),
        },
        {
          title: 'Clone',
          enabled: true,
          isDisabled: isCloningDunning,
          action: () => handleCloneDunning(dunning),
        },
        {
          title: 'Delete',
          color: 'danger' as const,
          enabled: true,
          action: () => handleDeleteDunning(dunning),
        },
      ];

      return (
        <MFlex alignItems="center">
          <DataTableActions actions={actions} />
        </MFlex>
      );
    },
    [
      isCloningDunning,
      handleUpdateDefaultDunningProcess,
      navigate,
      handleCloneDunning,
      handleDeleteDunning,
    ],
  );

  const columnContext = useMemo<DunningListColumnContext>(
    () => ({ actionBodyTemplate: actionsTemplate, billGroupsCounts }),
    [actionsTemplate, billGroupsCounts],
  );
  const { columns, state: columnState } = useTableColumns(
    DUNNING_LIST_CATALOG,
    columnContext,
  );

  return (
    <MPageContainer alignItems="stretch" data-testid="dunning-list-page">
      <MSettingsPageHeader title="Dunning">
        <MHStack gap="4">
          {!isTrulyEmptyList && (
            <>
              <MPageSearchInput
                placeholderKey="Name"
                value={globalFilter}
                onChange={(e: string) => setGlobalFilter(e)}
                count={dunningProcesses?.content.length}
              />
              <MButton
                variant="primary"
                onClick={redirectToCreate}
                data-testid="new-dunning-btn"
              >
                New
              </MButton>
            </>
          )}
        </MHStack>
      </MSettingsPageHeader>

      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={dunningProcesses?.content || []}
        isLoading={loading}
        enablePagination
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={dunningProcesses?.totalElements || 0}
        totalPages={dunningProcesses?.totalPages}
        pager={pager}
        setPager={setPager}
        onRowClick={(row, _tableRow, openInNewWindow) =>
          navigateOrOpenNewWindow(
            navigate,
            ROUTES.getDunningEditRoute(row.id),
            openInNewWindow,
          )
        }
        emptyProps={{
          mainMessage: 'Looks like there are no dunning processes here.',
          btnLabel: 'New Dunning Process',
          to: ROUTES.SETTINGS_DUNNING_NEW,
        }}
      />
    </MPageContainer>
  );
};
